import { supabase } from '../lib/supabase';

const BUCKET = 'moment_attachments';
const FIELDS = 'id, moment_id, kind, storage_path, file_name, mime_type, size_bytes, duration_seconds, created_at';

const safeName = (name) => name.replace(/[^A-Za-z0-9._-]+/g, '_').slice(-100) || 'file';

const removeObjects = async (paths) => {
  if (!paths.length) return;
  try {
    await supabase.storage.from(BUCKET).remove(paths);
  } catch (err) {
    // Best effort: the row is already gone, so the Moment is unaffected.
  }
};

// Strict version for permanent deletion: any failure is reported so nothing is half-deleted.
const removeObjectsStrict = async (paths) => {
  if (!paths.length) return;
  const { error } = await supabase.storage.from(BUCKET).remove(paths);
  if (error) throw error;
};

export const attachmentService = {
  removeObjects,
  removeObjectsStrict,

  // Upload first, then record it. If recording fails, the uploaded file is cleaned up.
  async upload(ownerId, momentId, item) {
    const path = `${ownerId}/${momentId}/${crypto.randomUUID()}-${safeName(item.name)}`;
    const { error: uploadError } = await supabase.storage
      .from(BUCKET)
      .upload(path, item.blob, { contentType: item.mime, upsert: false, cacheControl: '3600' });
    if (uploadError) throw uploadError;

    const { data, error } = await supabase
      .from('moment_attachments')
      .insert({
        moment_id: momentId,
        owner_id: ownerId,
        kind: item.kind,
        storage_path: path,
        file_name: item.name.slice(0, 255),
        mime_type: item.mime,
        size_bytes: item.size,
        duration_seconds: item.duration ?? null
      })
      .select(FIELDS)
      .single();
    if (error) {
      await removeObjects([path]);
      throw error;
    }
    return data;
  },

  async remove(attachment) {
    const { data, error } = await supabase
      .from('moment_attachments')
      .delete()
      .eq('id', attachment.id)
      .select('id');
    if (error) throw error;
    if (!data || data.length === 0) throw new Error('Attachment not found.');
    await removeObjects([attachment.storage_path]);
  },

  async signedUrls(paths) {
    const { data, error } = await supabase.storage.from(BUCKET).createSignedUrls(paths, 3600);
    if (error) throw error;
    return Object.fromEntries((data || []).filter((d) => d.signedUrl).map((d) => [d.path, d.signedUrl]));
  },

  async download(attachment) {
    const { data, error } = await supabase.storage
      .from(BUCKET)
      .createSignedUrl(attachment.storage_path, 60, { download: attachment.file_name });
    if (error) throw error;
    const a = document.createElement('a');
    a.href = data.signedUrl;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
};